import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Phone, Lock, BadgeCheck, Loader2 } from 'lucide-react';
import { useBuyerAuth } from '../contexts/BuyerAuthContext';
import { trackEvent } from '../utils/analytics';
import './ShowNumberButton.css';

/**
 * "Show Seller Number" — the only place on the site a seller's phone number
 * is ever shown to a customer.
 *
 * The number is not on the page waiting to be un-hidden; nothing is sent to the
 * browser until the dealer asks, and the ask is what gets logged. So this
 * component is a button that fetches, not a toggle that reveals — the
 * difference is the whole audit trail.
 *
 * Five states, because the honest answer differs at every stage:
 *   signed out          → sign in first
 *   not a dealer        → this is a dealer feature, here's how to apply
 *   pending             → we have your request, nothing to do but wait
 *   rejected            → why, and what you can still do
 *   approved            → the button that works
 *
 * The server is the authority on all of this — every branch here is a
 * courtesy so the dealer isn't clicking a button that will only 403.
 */
const ShowNumberButton = ({ vehicle }) => {
  const { buyer, token, API_URL, getAuthHeader, refreshBuyer } = useBuyerAuth();
  const location = useLocation();

  const [revealed, setRevealed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const signedIn = !!token;
  const isDealer = buyer?.buyer_type === 'dealer';
  const status = buyer?.dealer_status;

  const reveal = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/buyer/vehicles/${vehicle.id}/reveal-number`,
        {},
        { headers: getAuthHeader() }
      );
      setRevealed(res.data);
      trackEvent('seller_number_revealed', {
        vehicle_id: vehicle.id,
        contact_source: res.data.contact_source,
      });
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || 'Could not show the number. Please try again.');
      // Approval can be revoked while a dealer is mid-session. Re-reading the
      // profile makes the component fall into the right state on its own
      // instead of leaving them clicking a button that will never work.
      if (err.response?.status === 403) refreshBuyer();
    } finally {
      setLoading(false);
    }
  };

  // ── Revealed ───────────────────────────────────────────────────────────
  if (revealed) {
    const dialable = String(revealed.seller.phone).replace(/[^\d+]/g, '');
    return (
      <div className="show-number revealed" data-testid="seller-number-revealed">
        <span className="show-number-label">
          {revealed.contact_source === 'platform' ? 'Contact' : 'Seller'}
        </span>
        <span className="show-number-name">{revealed.seller.name}</span>
        <a href={`tel:${dialable}`} className="show-number-value" data-testid="seller-number-value">
          <Phone size={18} /> {revealed.seller.phone}
        </a>
      </div>
    );
  }

  // ── Signed out ─────────────────────────────────────────────────────────
  if (!signedIn) {
    return (
      <Link
        to="/buyer/login?type=dealer"
        state={{ from: location.pathname }}
        className="show-number-btn"
        data-testid="show-number-signin"
        onClick={() => trackEvent('show_number_click', { state: 'signed_out' })}
      >
        <Lock size={18} /> Show Seller Number
      </Link>
    );
  }

  // ── Signed in, not a dealer ────────────────────────────────────────────
  if (!isDealer) {
    return (
      <div className="show-number locked" data-testid="show-number-not-dealer">
        <p><Lock size={16} /> Seller numbers are for verified dealers.</p>
        <Link
          to="/buyer/register?type=dealer"
          className="show-number-link"
          onClick={() => trackEvent('show_number_click', { state: 'not_dealer' })}
        >
          <BadgeCheck size={16} /> Register as a dealer
        </Link>
      </div>
    );
  }

  // ── Dealer, awaiting review ────────────────────────────────────────────
  if (status !== 'approved' && status !== 'rejected') {
    return (
      <div className="show-number pending" data-testid="show-number-pending">
        <p><Lock size={16} /> Number hidden — your dealer access is under review.</p>
        <span className="show-number-note">We'll let you know as soon as it's approved.</span>
      </div>
    );
  }

  // ── Dealer, declined ───────────────────────────────────────────────────
  if (status === 'rejected') {
    return (
      <div className="show-number rejected" data-testid="show-number-rejected">
        <p><Lock size={16} /> Dealer access not approved.</p>
        {buyer?.dealer_rejection_reason && (
          <span className="show-number-note">{buyer.dealer_rejection_reason}</span>
        )}
        <span className="show-number-note">
          You can still enquire below and our team will help you directly.
        </span>
      </div>
    );
  }

  // ── Approved ───────────────────────────────────────────────────────────
  return (
    <div className="show-number approved">
      <button
        className="show-number-btn"
        onClick={reveal}
        disabled={loading}
        data-testid="show-number-btn"
      >
        {loading
          ? <><Loader2 size={18} className="show-number-spin" /> Showing…</>
          : <><Phone size={18} /> Show Seller Number</>}
      </button>
      {error && <span className="show-number-error" data-testid="show-number-error">{error}</span>}
    </div>
  );
};

export default ShowNumberButton;
